import React, { useMemo, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import { FlexRowBetween, TitleText } from 'components'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { EscalationPolicyService } from 'utility'
import { RecipientPicker } from 'page-components/escalation-policy'
import {
    buildWatcherPayload,
    getApiErrorMessage,
} from 'page-components/escalation-policy/utils'
import {
    FormHelper,
    ModalError,
    PolicyModalActions,
    SegBtn,
    Segmented,
} from 'page-components/escalation-policy/styles'

export const AddWatcherModal = ({ open, onClose, ticketId, onAdded }) => {
    const [mode, setMode] = useState('picker')
    const [typeId, setTypeId] = useState(1)
    const [recipientId, setRecipientId] = useState(null)
    const [vendorCompanyId, setVendorCompanyId] = useState(null)
    const [watcherEmail, setWatcherEmail] = useState('')
    const [externalEmail, setExternalEmail] = useState('')
    const [notifyOnCreated, setNotifyOnCreated] = useState(false)
    const [notifyOnUpdated, setNotifyOnUpdated] = useState(false)
    const [notifyOnEscalated, setNotifyOnEscalated] = useState(true)
    const [notifyOnResolved, setNotifyOnResolved] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const reset = () => {
        setMode('picker')
        setTypeId(1)
        setRecipientId(null)
        setVendorCompanyId(null)
        setWatcherEmail('')
        setExternalEmail('')
        setNotifyOnCreated(false)
        setNotifyOnUpdated(false)
        setNotifyOnEscalated(true)
        setNotifyOnResolved(false)
        setError('')
    }

    const canSubmit = useMemo(() => {
        if (mode === 'email') {
            return /.+@.+\..+/.test(String(externalEmail).trim())
        }
        const id = Number(recipientId)
        return Number.isFinite(id) && id > 0
    }, [mode, externalEmail, recipientId])

    const handleClose = () => {
        reset()
        onClose?.()
    }

    const handleSubmit = async () => {
        if (!canSubmit) return
        setSubmitting(true)
        setError('')
        try {
            const payload = buildWatcherPayload({
                ticketId,
                mode,
                typeId,
                recipientId,
                watcherEmail:
                    mode === 'email' ? externalEmail : watcherEmail,
                notifyOnCreated,
                notifyOnUpdated,
                notifyOnEscalated,
                notifyOnResolved,
            })
            await EscalationPolicyService.addTicketWatcher(
                Number(ticketId),
                payload,
            )
            onAdded?.()
            reset()
            onClose?.()
        } catch (err) {
            setError(
                err?.message && !err?.status
                    ? err.message
                    : getApiErrorMessage(err),
            )
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Add watcher</TitleText>
                    <X
                        className="pointer"
                        size={28}
                        onClick={handleClose}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form error={Boolean(error)}>
                    {error && <ModalError>{error}</ModalError>}
                    <Form.Field>
                        <label>Watcher type</label>
                        <Segmented>
                            <SegBtn
                                type="button"
                                $on={mode === 'picker'}
                                onClick={() => setMode('picker')}
                            >
                                User / team / vendor
                            </SegBtn>
                            <SegBtn
                                type="button"
                                $on={mode === 'email'}
                                onClick={() => setMode('email')}
                            >
                                External email
                            </SegBtn>
                        </Segmented>
                        <FormHelper>
                            Watchers get email when the ticket hits the events
                            you select below.
                        </FormHelper>
                    </Form.Field>
                    {mode === 'picker' ? (
                        <RecipientPicker
                            label="Watcher"
                            typeId={typeId}
                            recipientId={recipientId}
                            vendorCompanyId={vendorCompanyId}
                            onChange={({
                                typeId: nextType,
                                recipientId: nextId,
                                vendorCompanyId: nextVendor,
                                email,
                            }) => {
                                setTypeId(nextType)
                                setRecipientId(nextId)
                                setVendorCompanyId(nextVendor)
                                setWatcherEmail(email || '')
                            }}
                        />
                    ) : (
                        <Form.Field required>
                            <label>External email</label>
                            <Form.Input
                                type="email"
                                placeholder="escalation-cc@example.com"
                                value={externalEmail}
                                onChange={(_, { value }) =>
                                    setExternalEmail(value)
                                }
                            />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <label>Notify on</label>
                        <Form.Checkbox
                            label="Created"
                            checked={notifyOnCreated}
                            onChange={(_, { checked }) =>
                                setNotifyOnCreated(!!checked)
                            }
                        />
                        <Form.Checkbox
                            label="Updated"
                            checked={notifyOnUpdated}
                            onChange={(_, { checked }) =>
                                setNotifyOnUpdated(!!checked)
                            }
                        />
                        <Form.Checkbox
                            label="Escalated"
                            checked={notifyOnEscalated}
                            onChange={(_, { checked }) =>
                                setNotifyOnEscalated(!!checked)
                            }
                        />
                        <Form.Checkbox
                            label="Resolved"
                            checked={notifyOnResolved}
                            onChange={(_, { checked }) =>
                                setNotifyOnResolved(!!checked)
                            }
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <PolicyModalActions $end>
                <AppSecondaryButton
                    onClick={handleClose}
                    disabled={submitting}
                >
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={submitting || !canSubmit}
                >
                    Add watcher
                </AppPrimaryButton>
            </PolicyModalActions>
        </Modal>
    )
}
